/**
 * Dev-only rich-content render + UX harness (XON-96).
 *
 * Two jobs:
 *  1. Render proof - every renderable element draws with the REAL libraries in
 *     a REAL browser (vitest mocks them), via the real render components.
 *  2. UX review - the same visuals inside realistic card chrome (flashcard,
 *     quiz, highlight, mindspace question) so a Playwright screenshot pass can
 *     judge fit / sizing / contrast in light + dark, desktop + mobile.
 *
 * Dev-gated in App.tsx; never reachable in production. Dark mode is driven the
 * real way - the app's ThemeProvider reads the `ai-tutor-theme` localStorage
 * key - so the Playwright spec seeds that to capture dark screenshots.
 */
import ReactMarkdown from 'react-markdown';
import {
  BlockMarkdown,
  InlineMarkdown,
  REHYPE_PLUGINS,
  REMARK_PLUGINS,
  createMarkdownComponents,
} from '@/components/core/markdown/markdown';
import { BodyContent } from '@/components/features/highlights-template/_shared/BodyContent';

// One markdown fixture per renderable element.
const FIX = {
  math: 'Inline $E = mc^2$ and display: $$\\int_0^1 x^2\\,dx = \\tfrac13$$',
  code: '```python\nnums = [x * 2 for x in range(3)]\nprint(nums)\n```',
  table:
    '| Term | Meaning |\n| --- | --- |\n| CPU | processor |\n| RAM | memory |',
  mermaid:
    '```mermaid\ngraph TD; Stimulus-->Depolarization-->ActionPotential\n```',
  // The toy above is three short labels and fits any box, which is why this
  // harness never caught a sizing bug. These are the shapes the model actually
  // writes: long labels, maths, a long chain, a sequence, nested subgraphs.
  mermaidLong:
    '```mermaid\nflowchart TD\n  A["Photosynthesis converts light energy into chemical energy"] --> B["Glucose is stored in the chloroplast"]\n  B --> C["Cellular respiration releases ATP for the cell"]\n```',
  mermaidMath:
    '```mermaid\nflowchart LR\n  A["Rest energy $E = mc^2$"] --> B["Momentum $p = mv$"] --> C["Total $E^2 = (pc)^2 + (mc^2)^2$"]\n```',
  mermaidWide:
    '```mermaid\nflowchart LR\n  A[Input] --> B[Tokenize] --> C[Embed] --> D[Attention] --> E[FeedForward] --> F[Softmax] --> G[Output]\n```',
  mermaidSeq:
    '```mermaid\nsequenceDiagram\n  participant S as Student\n  participant T as Tutor\n  S->>T: Asks about mitosis\n  T-->>S: Explains prophase and metaphase\n```',
  mermaidSub:
    '```mermaid\nflowchart TD\n  subgraph L["Light-dependent reactions"]\n    A[Photon] --> B[ATP + NADPH]\n  end\n  subgraph D["Calvin cycle"]\n    C["CO2 fixation"] --> E[Glucose]\n  end\n  B --> C\n```',
  chart:
    '```chart\n{"mark":"bar","data":{"values":[{"a":"x","b":3},{"a":"y","b":5}]},' +
    '"encoding":{"x":{"field":"a","type":"nominal"},"y":{"field":"b","type":"quantitative"}}}\n```',
  funcplot: '```funcplot\ny = sin(x)\ny = x^2 - 3\n```',
  smiles: '```smiles\nC1=CC=CC=C1\n```',
  music: '```abc\nX:1\nK:C\nCDEF|GABc|\n```',
  geometry:
    '```geometry\n{"boundingbox":[-1,5,6,-1],"elements":[' +
    '{"type":"polygon","args":[[[0,0],[4,0],[0,3]]]},' +
    '{"type":"circle","args":[[0,0],1]}]}\n```',
  // The lesson prompt's own examples. Neither had a fixture, so neither had
  // ever been drawn in a browser before the prompt started asking for them.
  venn: '```venn\n{"sets":["Mitosis","Meiosis"],"only":[["2 identical cells"],["4 unique cells"]],"both":["DNA copied first"]}\n```',
  numberline:
    '```numberline\n{"min":-5,"max":5,"step":1,"ranges":[{"from":-1,"to":3,"openFrom":true}],"points":[{"at":2,"label":"x = 2"}]}\n```',
  // A ray: its arrowhead is sized in pixels, not stroke widths.
  numberlineRay:
    '```numberline\n{"min":0,"max":10,"step":1,"ranges":[{"from":4}],"points":[{"at":4,"label":"x \u2265 4"}]}\n```',
} as const;

type ElementKey = keyof typeof FIX;

/**
 * The lesson text surface, rendered the way the lesson actually renders it
 * (TextRenderer -> LessonMarkdown). NOT `BlockMarkdown`: that is the compact
 * CARD pipeline, which height-caps every figure and makes it enlargeable. The
 * two were one surface here, so a card-only sizing bug looked like lesson text
 * and neither could be told apart.
 */
const LESSON_COMPONENTS = createMarkdownComponents();

function LessonSurfaceMarkdown({ children }: { children: string }) {
  return (
    <ReactMarkdown
      remarkPlugins={REMARK_PLUGINS}
      rehypePlugins={REHYPE_PLUGINS}
      components={LESSON_COMPONENTS}
    >
      {children}
    </ReactMarkdown>
  );
}
const ALL: ElementKey[] = [
  'math',
  'code',
  'table',
  'mermaid',
  'mermaidLong',
  'mermaidMath',
  'mermaidWide',
  'mermaidSeq',
  'mermaidSub',
  'chart',
  'funcplot',
  'smiles',
  'music',
  'geometry',
  'venn',
  'numberline',
  'numberlineRay',
];

function Cell({
  surface,
  element,
  wide,
  children,
}: {
  surface: string;
  element: string;
  /** The lesson's reading column, not a tile: a figure is sized to the width
   *  it is given, so a 320px tile shrinks one the real column draws full size.
   */
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      data-testid={`el-${surface}-${element}`}
      className={`m-2 rounded-lg border border-border p-3 ${
        wide ? 'w-[720px] max-w-full' : 'min-w-[320px]'
      }`}
    >
      <div className='mb-1.5 text-xs text-muted-foreground'>
        {surface} · {element}
      </div>
      {children}
    </div>
  );
}

function Section({
  surface,
  title,
  els,
  as,
}: {
  surface: string;
  title: string;
  els: ElementKey[];
  as: 'lesson' | 'block' | 'inline' | 'bodycontent';
}) {
  return (
    <section data-testid={`surface-${surface}`} className='mb-8'>
      <h2 className='text-lg font-semibold'>{title}</h2>
      <div className='flex flex-wrap'>
        {els.map(el => (
          <Cell key={el} surface={surface} element={el} wide={as === 'lesson'}>
            {as === 'lesson' && (
              <LessonSurfaceMarkdown>{FIX[el]}</LessonSurfaceMarkdown>
            )}
            {as === 'block' && <BlockMarkdown>{FIX[el]}</BlockMarkdown>}
            {as === 'inline' && <InlineMarkdown>{FIX[el]}</InlineMarkdown>}
            {as === 'bodycontent' && <BodyContent body={FIX[el]} />}
          </Cell>
        ))}
      </div>
    </section>
  );
}

/* ---- In-context cards: mirror the real artifact chrome for UX review ---- */

// Mirrors LessonFlashcardRunner's card faces.
function FlashcardMock({ face, body }: { face: string; body: string }) {
  return (
    <div className='flex h-72 w-80 flex-col items-center justify-center rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-purple-50 p-8 text-center shadow-sm dark:border-border dark:bg-card dark:bg-none'>
      <div className='mb-2 text-xs uppercase tracking-wide text-muted-foreground'>
        {face}
      </div>
      <BlockMarkdown className='text-xl font-medium text-foreground'>
        {body}
      </BlockMarkdown>
    </div>
  );
}

// Mirrors LessonQuizRunner / mindspace PracticeRunner question + options.
function QuizMock({
  question,
  options,
}: {
  question: string;
  options: string[];
}) {
  return (
    <div className='w-full max-w-[28rem] rounded-2xl border border-border/60 p-6'>
      <BlockMarkdown className='mb-5 text-xl font-medium text-foreground'>
        {question}
      </BlockMarkdown>
      <ul className='space-y-2'>
        {options.map((o, i) => (
          <li
            key={i}
            className='flex items-center gap-3 rounded-lg border border-border px-4 py-3 text-sm'
          >
            <span className='font-mono text-muted-foreground'>
              {String.fromCharCode(65 + i)}
            </span>
            <span className='flex-1'>
              <InlineMarkdown>{o}</InlineMarkdown>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Mirrors a highlight carousel card (mobile-ish width).
function HighlightMock({ title, body }: { title: string; body: string }) {
  return (
    <div className='w-72 rounded-2xl border border-border bg-card p-5 shadow-sm'>
      <h4 className='mb-3 text-base font-semibold text-foreground'>{title}</h4>
      <BodyContent body={body} className='text-sm text-muted-foreground' />
    </div>
  );
}

function InContext() {
  return (
    <section data-testid='surface-incontext' className='mb-8'>
      <h2 className='mb-3 text-lg font-semibold'>
        In-context cards (real chrome) — UX review
      </h2>
      <div className='flex flex-wrap items-start gap-6'>
        <div data-testid='ctx-flashcard-front'>
          <FlashcardMock face='Question' body='Which molecule is this?' />
        </div>
        <div data-testid='ctx-flashcard-back'>
          <FlashcardMock face='Answer' body={FIX.smiles} />
        </div>
        <div data-testid='ctx-quiz'>
          <QuizMock
            question={'What does this graph show?\n\n' + FIX.funcplot}
            options={[
              'A line $y=x$',
              'A parabola',
              '`sin(x)` and a parabola',
              'A circle',
            ]}
          />
        </div>
        <div data-testid='ctx-mindspace'>
          <QuizMock
            question={'Identify the compound:\n\n' + FIX.smiles}
            options={['Benzene', 'Methane', 'Ethanol', 'Water']}
          />
        </div>
        <div data-testid='ctx-highlight'>
          <HighlightMock title='Key curve' body={FIX.funcplot} />
        </div>
      </div>
    </section>
  );
}

export default function RichRenderHarness() {
  return (
    <div className='min-h-screen bg-background text-foreground'>
      <div className='mx-auto max-w-6xl p-6'>
        <h1 className='mb-1 text-2xl font-bold'>Rich-content render harness</h1>
        <p className='mb-6 text-muted-foreground' data-testid='harness-ready'>
          Real components + real libraries. Cells prove rendering; the
          in-context cards are for UX review.
        </p>

        <InContext />

        <Section
          surface='lesson-text'
          title='Lesson text / Chat (the lesson’s own renderers) — all elements'
          els={ALL}
          as='lesson'
        />
        <Section
          surface='card-body'
          title='Quiz stem / Flashcard / Glossary def / Mindspace (BlockMarkdown) — visuals'
          els={[
            'math',
            'code',
            'table',
            'mermaid',
            'mermaidLong',
            'mermaidWide',
            'mermaidSeq',
            'mermaidSub',
            'chart',
            'funcplot',
            'smiles',
            'geometry',
          ]}
          as='block'
        />
        <Section
          surface='highlights'
          title='Highlights (BodyContent) — visuals'
          els={[
            'smiles',
            'funcplot',
            'chart',
            'mermaid',
            'mermaidWide',
            'mermaidSub',
            'geometry',
          ]}
          as='bodycontent'
        />
        <Section
          surface='option'
          title='Quiz option chips (InlineMarkdown) — inline only'
          els={['math', 'code']}
          as='inline'
        />
      </div>
    </div>
  );
}
